import React, { Suspense } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { AppRoutes } from './router';
import { AuthProvider } from './auth/AuthProvider';
import { NotificationProvider } from './contexts/NotificationContext';
import { livestockTheme } from './theme';
import { ForgeProvider } from '@design2code/forge-ui';
import { ForgeFloatingButton } from '@design2code/forge-ui';
import { PSCWidget } from '@design2code/psc-widget';
import '@design2code/psc-widget/styles';

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Application error:', error, info);
  }
  override render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 3, textAlign: 'center', bgcolor: '#FAFAF5', color: '#1A1A1A' }}>
          <h1>Something went wrong</h1>
          <p style={{ color: '#6B7280', maxWidth: 500 }}>An unexpected error occurred. Please refresh the page.</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '8px 16px', backgroundColor: '#1B5E20', color: '#FFFFFF', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
            Refresh Page
          </button>
        </Box>
      );
    }
    return this.props.children;
  }
}

const App: React.FC = () => (
  <ThemeProvider theme={livestockTheme}>
    <CssBaseline />
    <NotificationProvider>
      <ErrorBoundary>
        <AuthProvider>
          <ForgeProvider serviceId="livestock" forgeApiUrl="/api/v1/forge">
            <Suspense fallback={<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: '#FAFAF5' }}><CircularProgress sx={{ color: '#1B5E20' }} /></Box>}>
              <AppRoutes />
            </Suspense>
            <ForgeFloatingButton position="bottom-right" serviceId="livestock" />
            <PSCWidget config={{ appName: 'LivestockFarmManager', apiBaseUrl: '/api/v1/forge', position: 'bottom-left' }} />
          </ForgeProvider>
        </AuthProvider>
      </ErrorBoundary>
    </NotificationProvider>
  </ThemeProvider>
);

export default App;
